import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./components/Authentication/AuthContext";
import PrivateRoute from "./components/Authentication/PrivateRoute";
import Layout from "./components/Common/Layout";
import { ToastContainer } from "react-toastify";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "./api/interceptors";
import { store } from "./redux/store";
import { Provider } from "react-redux";
import { Toaster } from "sonner";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import LoadingFallback from "./components/Common/LoadingFallback";

// Create a wrapper component that applies ErrorBoundary and Suspense
const RouteWrapper = ({ component: Component }) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <Component />
    </Suspense>
  </ErrorBoundary>
);

// Lazy-loaded components
const Register = lazy(() => import("./components/Authentication/Register"));
const Login = lazy(() => import("./components/Authentication/Login"));
const SocialLogin = lazy(() =>
  import("./components/Authentication/SocialLogin")
);
const VerifyOtp = lazy(() => import("./components/Authentication/VerifyOtp"));
const VerifyOtpForgotPassword = lazy(() =>
  import("./components/Authentication/VerifyOtpForgotPassword")
);
const ForgotPassword = lazy(() =>
  import("./components/Authentication/ForgotPassword")
);
const ResetPassword = lazy(() =>
  import("./components/Authentication/ResetPassword")
);
const Home = lazy(() => import("./components/Home/Home"));
const MainMenu = lazy(() => import("./components/MainMenu"));
const UpswapFeatures = lazy(() => import("./components/UpswapFeatures"));
const MyActivities = lazy(() => import("./components/Activities/MyActivities"));
const MyPersonalAccount = lazy(() => import("./components/MyPersonalAccount"));
const UserProfile = lazy(() => import("./components/UserProfile"));
const UpswapVendors = lazy(() => import("./components/UpswapVendors"));
const DealsPage = lazy(() => import("./components/DealsPage"));
const Discover = lazy(() => import("./components/Dashboard/DiscoverPage"));
const ExclusiveDeals = lazy(() =>
  import("./components/Dashboard/ExclusiveDeals")
);
const Activities = lazy(() => import("./components/Dashboard/Activities"));
const Vendor = lazy(() => import("./components/Dashboard/Vendor"));
const PopularVendors = lazy(() =>
  import("./components/Dashboard/PopularVendors")
);
const VendorList = lazy(() => import("./components/Dashboard/VendorList"));
const VendorDashboard = lazy(() => import("./components/VendorDashboard"));
const StateSection = lazy(() => import("./components/Dashboard/StateSection"));
const PopularCategories = lazy(() =>
  import("./components/Dashboard/PopularCategories ")
);
const HowItWorks = lazy(() => import("./components/Dashboard/HowItWorks"));
const CommunitySection = lazy(() =>
  import("./components/Dashboard/CommunitySection")
);
const Settings = lazy(() => import("./components/Settings"));
const ContactSupport = lazy(() => import("./components/ContactSupport"));
const BasicInfo = lazy(() => import("./components/VendorKYC/BasicInfo"));
const VendorDocument = lazy(() =>
  import("./components/VendorKYC/VendorDocument")
);
const BankDetails = lazy(() => import("./components/VendorKYC/BankDetails"));
const ServiceTime = lazy(() => import("./components/VendorKYC/ServiceTime"));
const AddAddress = lazy(() => import("./components/VendorKYC/AddAddress"));
const AddService = lazy(() => import("./components/VendorKYC/AddService"));
const PostDeal = lazy(() => import("./components/Deals/PostDeal"));
const PostDealNext = lazy(() => import("./components/Deals/PostDealNext"));
const MyDeals = lazy(() => import("./components/Deals/MyDeals"));
const PopularDeals = lazy(() => import("./components/Deals/PopularDeals"));
const Favorite = lazy(() => import("./components/Dashboard/Favorite"));
const Unfavorite = lazy(() => import("./components/Unfavorite"));
const VendorProfile = lazy(() => import("./components/VendorProfile"));
const PostActivitiesnext = lazy(() =>
  import("./components/Activities/PostActivitiesnext")
);
const ActivitiesPage = lazy(() =>
  import("./components/Activities/ActivitiesPage")
);
const BrowseService = lazy(() => import("./components/Services/BrowseService"));
const FavoriteService = lazy(() => import("./components/Services/FavoriteService"));
const VendorHomePageDashboard = lazy(() =>
  import("./components/Vendor/VendorHomePageDashboard")
);
const DealDetailsPage = lazy(() =>
  import("./components/Vendor/VendorDeal/DealDetailsPage/DealDetailsPage")
);
const VendorServiceViewWrapper = lazy(() =>
  import("./components/Vendor/VendorServices/View/VendorServiceViewWrapper")
);
const AddDealFormWrapper = lazy(() =>
  import("./components/Vendor/VendorDeal/Add/AddDealFormWrapper")
);
const ActivityAddFormWrapper = lazy(() =>
  import("./components/Customer/Activities/Add/ActivityAddFormWrapper")
);
const ActivitiesDetailView = lazy(() =>
  import("./components/Customer/Activities/ActivitiesDetailView")
);
const ActivityChatRoom = lazy(() =>
  import("./components/Customer/Activities/ActivityChatRoom/ActivityChatRoom")
);
const ActivitiesTabs = lazy(() =>
  import("./components/Customer/Activities/BrowseActivities/ActivitiesTabs")
);
const CreateAccount = lazy(() =>
  import("./components/Authentication/LoginRegister/CreateAccount")
);
const AccountCreateing = lazy(() =>
  import("./components/Authentication/LoginRegister/AccountCreateing")
);
const OtpVerifyCreateAccount = lazy(() =>
  import("./components/Authentication/LoginRegister/OtpVerifyCreateAccount")
);
const OtpVerifyPhoneLogin = lazy(() =>
  import("./components/Authentication/LoginRegister/OtpVerifyPhoneLogin")
);
const ActivityChats = lazy(() => import("./components/Admin/ActivityChats"));
const Participants = lazy(() => import("./components/Admin/Participants"));
const ManageActivityDetails = lazy(() =>
  import("./components/Admin/ManageActivityDetails")
);
const ServiceDetailView = lazy(() =>
  import("./components/Services/ServiceDetailView")
);
const MyPurchaseDealView = lazy(() =>
  import("./components/Customer/Deal/MyPurchaseDealView")
);
const MySales = lazy(() =>
  import("./components/Vendor/VendorDeal/DealSales/MyDealSale")
);
const ProviderManagementPageWrapper = lazy(() =>
  import("./components/Vendor/ProviderManagement/ProviderManagementPageWrapper")
);
const ProviderAddFormWrapper = lazy(() =>
  import("./components/Vendor/ProviderManagement/Add/ProviderAddFormWrapper")
);

const AppointmentBooking = lazy(() =>
  import("./components/Customer/Appointment/AppointmentBooking")
);
const AppointmentConfirmation = lazy(() =>
  import("./components/Customer/Appointment/AppointmentBookingConfirmation")
);
const CustomerBookings = lazy(() =>
  import("./components/Customer/Appointment/CustomerBookings")
);
const VendorAppointmentsList = lazy(() =>
  import("./components/Vendor/Appointment/VendorAppointmentsList")
);

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <GoogleOAuthProvider clientId="241793290888-p8ba5p5ucgn1f5mj8un3h71u6a52chkg.apps.googleusercontent.com">
          <Router>
            <Toaster position="top-right" expand={true} richColors />
            <ToastContainer
              position="top-center"
              autoClose={1000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />

            <Routes>
              <Route element={<Layout />}>
                {/* Public Routes */}
                <Route path="/" element={<RouteWrapper component={Home} />} />
                <Route element={<PrivateRoute isAuthRoute={true} />}>
                  <Route
                    path="/login"
                    element={<RouteWrapper component={Login} />}
                  />
                  <Route
                    path="/createAccount"
                    element={<RouteWrapper component={CreateAccount} />}
                  />
                  <Route
                    path="/OtpVerifyPage"
                    element={
                      <RouteWrapper component={OtpVerifyCreateAccount} />
                    }
                  />
                  <Route
                    path="/PhoneOtp"
                    element={<RouteWrapper component={OtpVerifyPhoneLogin} />}
                  />
                  <Route
                    path="/resgisterAccount"
                    element={<RouteWrapper component={AccountCreateing} />}
                  />
                  <Route
                    path="/Register"
                    element={<RouteWrapper component={Register} />}
                  />
                  <Route
                    path="/SocialLogin"
                    element={<RouteWrapper component={SocialLogin} />}
                  />
                  <Route
                    path="/VerifyOtp"
                    element={<RouteWrapper component={VerifyOtp} />}
                  />
                  <Route
                    path="/VerifyOtpForgotPassword"
                    element={
                      <RouteWrapper component={VerifyOtpForgotPassword} />
                    }
                  />
                  <Route
                    path="/ForgotPassword"
                    element={<RouteWrapper component={ForgotPassword} />}
                  />
                  <Route
                    path="/ResetPassword"
                    element={<RouteWrapper component={ResetPassword} />}
                  />
                </Route>

                <Route
                  path="/ActivitiesDetails/:activityId"
                  element={<RouteWrapper component={ActivitiesDetailView} />}
                />
                <Route
                  path="/chat/:activityId/:chatroomId"
                  element={<RouteWrapper component={ActivityChatRoom} />}
                />
                <Route
                  path="/DealDetails/:deal_uuid"
                  element={<RouteWrapper component={DealDetailsPage} />}
                />
                <Route
                  path="/Home"
                  element={<RouteWrapper component={Home} />}
                />
                <Route
                  path="/UpswapVendors"
                  element={<RouteWrapper component={UpswapVendors} />}
                />
                <Route
                  path="/vendor/:userId"
                  element={<RouteWrapper component={VendorProfile} />}
                />
                <Route
                  path="/vendor/dashboard"
                  element={<RouteWrapper component={VendorHomePageDashboard} />}
                />
                <Route
                  path="/vendor/services"
                  element={
                    <RouteWrapper component={VendorServiceViewWrapper} />
                  }
                />
                <Route
                  path="/vendor/deal/add"
                  element={<RouteWrapper component={AddDealFormWrapper} />}
                />
                <Route
                  path="/vendor/activity/add"
                  element={<RouteWrapper component={ActivityAddFormWrapper} />}
                />
                <Route
                  path="/VendorProfile/:vendorId"
                  element={<RouteWrapper component={VendorProfile} />}
                />
                <Route
                  path="/PopularDeals"
                  element={<RouteWrapper component={PopularDeals} />}
                />
                <Route
                  path="/StateSection"
                  element={<RouteWrapper component={StateSection} />}
                />
                <Route
                  path="/PopularCategories"
                  element={<RouteWrapper component={PopularCategories} />}
                />
                <Route
                  path="MyPersonalAccount"
                  element={<RouteWrapper component={MyPersonalAccount} />}
                />
                <Route
                  path="/LocalActivities"
                  element={<RouteWrapper component={ActivitiesTabs} />}
                />
                <Route
                  path="/Vendor"
                  element={<RouteWrapper component={Vendor} />}
                />
                <Route
                  path="/VendorList"
                  element={<RouteWrapper component={VendorList} />}
                />
                <Route
                  path="/PopularVendors"
                  element={<RouteWrapper component={PopularVendors} />}
                />
                <Route
                  path="/Activities"
                  element={<RouteWrapper component={Activities} />}
                />
                <Route
                  path="/ExclusiveDeals"
                  element={<RouteWrapper component={ExclusiveDeals} />}
                />
                <Route
                  path="/Favorite"
                  element={<RouteWrapper component={Favorite} />}
                />
                <Route
                  path="/Unfavorite"
                  element={<RouteWrapper component={Unfavorite} />}
                />
                <Route
                  path="/VendorDashboard"
                  element={<RouteWrapper component={VendorDashboard} />}
                />
                <Route
                  path="/UserProfile"
                  element={<RouteWrapper component={UserProfile} />}
                />
                <Route
                  path="/HowItWorks"
                  element={<RouteWrapper component={HowItWorks} />}
                />
                <Route
                  path="/CommunitySection"
                  element={<RouteWrapper component={CommunitySection} />}
                />
                <Route
                  path="/PostActivities"
                  element={<RouteWrapper component={ActivityAddFormWrapper} />}
                />
                <Route
                  path="/PostActivitiesnext"
                  element={<RouteWrapper component={PostActivitiesnext} />}
                />

                {/* Protected Routes */}
                <Route
                  path="/MyActivities/:filterType?"
                  element={<RouteWrapper component={MyActivities} />}
                />
                <Route
                  element={<PrivateRoute allowedRoles={["vendor", "user"]} />}
                >
                  <Route
                    path="/Discover"
                    element={<RouteWrapper component={Discover} />}
                  />
                  <Route
                    path="/MainMenu"
                    element={<RouteWrapper component={MainMenu} />}
                  />
                  <Route
                    path="/UpswapFeatures"
                    element={<RouteWrapper component={UpswapFeatures} />}
                  />
                  <Route
                    path="/ContactSupport"
                    element={<RouteWrapper component={ContactSupport} />}
                  />
                  <Route
                    path="/Settings"
                    element={<RouteWrapper component={Settings} />}
                  />
                  <Route
                    path="/DealsPage"
                    element={<RouteWrapper component={DealsPage} />}
                  />
                  <Route
                    path="/BasicInfo"
                    element={<RouteWrapper component={BasicInfo} />}
                  />
                  <Route
                    path="/VendorDocument"
                    element={<RouteWrapper component={VendorDocument} />}
                  />
                  <Route
                    path="/BankDetails"
                    element={<RouteWrapper component={BankDetails} />}
                  />
                  <Route
                    path="/ServiceTime"
                    element={<RouteWrapper component={ServiceTime} />}
                  />
                  <Route
                    path="/AddAddress"
                    element={<RouteWrapper component={AddAddress} />}
                  />
                  <Route
                    path="/AddService"
                    element={<RouteWrapper component={AddService} />}
                  />
                  <Route
                    path="/PostDeal"
                    element={<RouteWrapper component={PostDeal} />}
                  />
                  <Route
                    path="/PostDealNext"
                    element={<RouteWrapper component={PostDealNext} />}
                  />
                  <Route
                    path="/MyDeals/:type?"
                    element={<RouteWrapper component={MyDeals} />}
                  />
                  <Route
                    path="/mypurchasedeal"
                    element={<RouteWrapper component={MyPurchaseDealView} />}
                  />
                  <Route
                    path="/mySalesDeal"
                    element={<RouteWrapper component={MySales} />}
                  />
                  <Route
                    path="/services/browse"
                    element={<RouteWrapper component={BrowseService} />}
                  />
                  <Route
                    path="/services/:serviceId"
                    element={<RouteWrapper component={ServiceDetailView} />}
                  />
                  <Route
                    path="/services/favorite"
                    element={<RouteWrapper component={FavoriteService} />}
                  />
                   <Route
                    path="/customer/appointment/:serviceId"
                    element={<RouteWrapper component={AppointmentBooking} />}
                  />
                   <Route
                    path="/appointment/confirmation/:appointmentId"
                    element={<RouteWrapper component={AppointmentConfirmation} />}
                  />
                  <Route
                    path="/customer/bookings"
                    element={<RouteWrapper component={CustomerBookings} />}
                  />
                   <Route
                    path="/appointments"
                    element={<RouteWrapper component={VendorAppointmentsList} />}
                  />
                  <Route
                    path="/ActivitiesPage"
                    element={<RouteWrapper component={ActivitiesPage} />}
                  />
                  <Route
                    path="/ActivityChats"
                    element={<RouteWrapper component={ActivityChats} />}
                  />
                  <Route
                    path="/ActivityRequests"
                    element={<RouteWrapper component={ManageActivityDetails} />}
                  />
                  <Route
                    path="/Participants"
                    element={<RouteWrapper component={Participants} />}
                  />
                  <Route
                    path="/ActivityRequests/:activityId"
                    element={<RouteWrapper component={ManageActivityDetails} />}
                  />
                  <Route
                    path="/vendor/providers"
                    element={
                      <RouteWrapper component={ProviderManagementPageWrapper} />
                    }
                  />
                  <Route
                    path="/vendor/providers/add"
                    element={
                      <RouteWrapper component={ProviderAddFormWrapper} />
                    }
                  />
                  <Route
                    path="/vendor/providers/edit/:providerId"
                    element={
                      <RouteWrapper component={ProviderAddFormWrapper} />
                    }
                  />
                </Route>
              </Route>
            </Routes>
          </Router>
        </GoogleOAuthProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
